import { ISODateString } from "../date";
import { CongSettings } from "./settings";
import { Tag } from "./tags";

export interface UserTerritory extends TerritoryRecord {
  territory: Territory;
}

export interface TerritoryRecord {
  id: number;
  congregation_id: number;
  territory_id: number;
  user_id: number;
  start: ISODateString | null; // not really ever null except when we generate pseudo-records
  end: ISODateString | null;
  notes?: string;
  tags: Tag[];
}

export type TerritoryBulkCheckinFailure = {
  id: number;
  error: string;
};

export type TerritoryBulkCheckinResponse = {
  successful: number[];
  failed: TerritoryBulkCheckinFailure[];
};

export interface TerritoryRequest {
  id: number;
  userId: number;
  requested: ISODateString;
  notes: string;
}

export type TerritoryShare = {
  id: number;
  territoryId: number;
  sharedWithUserId: number;
  end: ISODateString;
  notes?: string;
};

export type Territory = {
  id: number;
  number: string;
  locality: string;
  business: boolean;
  url?: string;
  boundaries?: string;
  annotations?: string;
  notes?: string;
  addresses?: TerritoryAddress[];
  disabled: boolean;
  tags: Tag[];
  children?: Territory[];
  parentId?: number;
  shares?: TerritoryShare[];
};
export const defaultSortOrder = 9999;
export type TerritoryAddress = {
  id: number;
  territoryId: number;
  sortOrder: number;
  line1: string;
  line2?: string;
  city?: string;
  state?: string;
  postalcode?: string;
  notes?: string;
  name?: string;
  phone?: string;
  lastWorked?: ISODateString;
  lastWorkedBy?: number;
  dnc: boolean;
  lang?: string;
  status?: TerritoryAddressStatus;
  locationType?: TerritoryAddressLocationType;
  location?: MapPoint;
  hideOnMap: boolean;
  pinLabel?: string;
  tags: Tag[];
  visits?: TerritoryAddressVisit[];
};

export type TerritoryMoveAddresses = {
  addressIds: number[];
  newTerritoryId: number;
};

export type TerritoryAddressSortOrder = Map<number, number>;

export enum TerritoryAddressLocationType {
  Business = "business",
  Residence = "residence",
  Custom = "custom",
}

export enum TerritoryAddressStatus {
  NotHome = "nh",
  ReturnVisit = "rv",
  NotInterested = "ni",
  Worked = "worked",
}

export type MapPoint = {
  x: number;
  y: number;
  srid: number;
};

export interface TerritoryThings {
  settings: CongSettings;
  businessOnly: boolean;
  setBusinessOnly: (set: boolean) => void;
}

export type TerritoryCong = {
  boundaries: string;
  notes?: string;
};

export type TerritoryImport = {
  territories: Territory[];
  records: TerritoryRecord[];
};

export enum HGLayerType {
  Boundary,
  Annotation,
}

export type TerritoryAddressStatistics = {
  count: number;
  dncCount: number;
};

export type TerritoryAddressVisit = {
  id: number;
  territoryAddressId: number;
  notes?: string;
  visitedAt: ISODateString;
  visitedTime?: string;
  userId: number;
  status: TerritoryAddressStatus;
};
export type TerritoryShareToken = {
  token: string;
  tokenHash: string;
  territoryId: number;
  expiresAt: string;
  created_at: string;
};
type TerritoryShareTokenOverview = {
  tokenHash: string;
  territoryId: number;
  expiresAt: string;
  createdAt: string;
};

export type TerritoryShareTokenListing = TerritoryShareToken | TerritoryShareTokenOverview;

export type SharedTerritoryResponse = {
  territory: Territory;
  settings: CongSettings;
  cong: { name: string; locale: { code: string } };
  tags: Tag[];
};

export type TerritoryTransferRequest = {
  id: number;
  territoryIds: number[];
};

export type RedactedTransferCongregation = {
  name: string;
  number: number;
  guid: string;
};

export type RedactedTransferTerritory = {
  territoryId: number;
  number: string;
  locality: string;
  business: boolean;
  disabled: boolean;
  addressCount: number;
  checkedOut: boolean;
  children?: RedactedTransferTerritory[];
};

export type TerritoryTransfer = {
  id: number;
  code: string;
  completed: boolean;
  createdAt: string;
  completedAt?: string | null;
  fromCongregation: RedactedTransferCongregation;
  toCongregation?: RedactedTransferCongregation | null;
  territories: RedactedTransferTerritory[];
};

export function blankTerritoryTransfer(): TerritoryTransfer {
  return {
    id: 0,
    code: "",
    completed: false,
    createdAt: "",
    fromCongregation: { name: "", number: 0, guid: "" },
    territories: [],
  };
}
